import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useThumbnailCache } from "hooks/useThumbnailCache";
import { rtctApi } from "services/rtctApi";
import { fieldTime } from "utils/fieldFormat";
import { useStableState } from "utils/stableState";

// Photos on the node page: the newest capture's pair as thumbnails (GET
// frames/, the Field cameras page's own source; the thumbnails load as
// authenticated blobs through useThumbnailCache). Playback, zoom and the
// lens view live on Field cameras.
const POLL_MS = 5 * 60 * 1000;

export function newestCapture(data) {
  const captures = Array.isArray(data?.captures) ? data.captures : [];
  return (
    captures
      .filter((c) => Array.isArray(c?.cameras) && c.cameras.length)
      .sort((a, b) => Date.parse(b.captured_at || 0) - Date.parse(a.captured_at || 0))[0] || null
  );
}

export default function LatestPhotos({ mac }) {
  const [state, setState] = useStableState({ mac: "", capture: null, count: null, error: "" });
  useEffect(() => {
    if (!mac || typeof rtctApi.frames !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      Promise.resolve(rtctApi.frames(mac, signal))
        .then((data) => {
          if (!signal.aborted)
            setState({ mac, capture: newestCapture(data), count: Number.isFinite(data?.count) ? data.count : null, error: "" });
        })
        .catch((e) => {
          if (!signal.aborted) setState((old) => ({ ...old, mac, error: e?.message || "Photos unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [mac, setState]);
  const capture = state.mac === mac ? state.capture : null;
  const images = useMemo(
    () =>
      (capture?.cameras || [])
        .filter((c) => !c.thumbnail_expired && c.thumbnail_url)
        .map((c) => ({ id: c.id, camera: c.camera, thumbnail_url: c.thumbnail_url })),
    [capture],
  );
  const urls = useThumbnailCache(images);
  return (
    <section className="field-panel" aria-label="Photos">
      <div className="field-panel-head">
        <div>
          <h2>Photos</h2>
          <p>{capture ? `Latest pair, ${fieldTime(capture.captured_at)} SGT` : "Latest pair"}</p>
        </div>
        {state.count !== null && state.mac === mac && <span className="field-state">{state.count} captures</span>}
      </div>
      <div className="field-panel-body">
        {state.error && state.mac === mac && <div className="field-alert">{state.error}</div>}
        {!capture && !state.error && <div className="field-notice">No photo yet.</div>}
        {capture && (
          <div className="field-node-photos">
            {(capture.cameras || []).map((c) => (
              <figure key={c.id}>
                {urls[String(c.id)] ? (
                  <img src={urls[String(c.id)]} alt={`Camera ${c.camera}, ${fieldTime(capture.captured_at)} SGT`} />
                ) : (
                  <div className="field-node-photo-wait">{c.thumbnail_expired ? "no longer kept" : "loading…"}</div>
                )}
                <figcaption>Camera {c.camera}</figcaption>
              </figure>
            ))}
          </div>
        )}
        <p className="field-caption">
          All photos in <Link to="/field-cameras">Field cameras</Link>.
        </p>
      </div>
    </section>
  );
}
